import type { ReactNode } from 'react';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LeadStatus, LeadSource } from '../../types';
import {
  useLeads,
  statusColors,
  statusLabels,
  sourceLabels,
  getScoreColor,
  formatLeadDate,
} from '../../hooks/useLeads';

type SortField = 'name' | 'email' | 'source' | 'status' | 'score' | 'assigned_to' | 'created_at';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

export function LeadList(): ReactNode {
  const navigate = useNavigate();
  
  // Filters state
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState<LeadSource | ''>('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [search, setSearch] = useState('');
  
  // Sort state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'created_at',
    direction: 'desc',
  });

  const {
    leads,
    loading,
    error,
    pagination,
    setPage,
    refreshData,
  } = useLeads({
    filters: {
      status: statusFilter || undefined,
      source: sourceFilter || undefined,
      assignee: assigneeFilter || undefined,
    },
    sort: sortConfig,
    pageSize: 20,
  });

  // Client-side search filtering
  const filteredLeads = useMemo(() => {
    if (!search.trim()) return leads;
    
    const searchLower = search.toLowerCase();
    return leads.filter(lead => {
      const name = lead.metadata?.name || '';
      const email = lead.metadata?.email || '';
      const company = lead.metadata?.company || '';
      
      return (
        name.toLowerCase().includes(searchLower) ||
        email.toLowerCase().includes(searchLower) ||
        company.toLowerCase().includes(searchLower) ||
        lead.source.toLowerCase().includes(searchLower) ||
        lead.status.toLowerCase().includes(searchLower)
      );
    });
  }, [leads, search]);

  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 text-ds-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-ds-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const handleRowClick = (leadId: string) => {
    navigate(`/leads/${leadId}`);
  };

  const totalPages = pagination.total > 0 
    ? Math.ceil(pagination.total / pagination.pageSize)
    : pagination.hasMore ? pagination.page + 1 : pagination.page;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Leads</h1>
        <button className="btn btn-primary">+ New Lead</button>
      </div>

      {/* Search & Filters */}
      <div className="card mb-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search leads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LeadStatus | '')}
            className="min-w-[150px]"
          >
            <option value="">All Statuses</option>
            {Object.entries(statusLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as LeadSource | '')}
            className="min-w-[150px]"
          >
            <option value="">All Sources</option>
            {Object.entries(sourceLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Assigned to..."
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className="min-w-[150px]"
          />
          <button
            onClick={() => {
              setStatusFilter('');
              setSourceFilter('');
              setAssigneeFilter('');
              setSearch('');
            }}
            className="btn btn-secondary"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
          {error}
          <button onClick={refreshData} className="ml-2 underline">Retry</button>
        </div>
      )}

      {/* Lead Table */}
      <div className="card overflow-hidden">
        {loading && leads.length === 0 ? (
          <div className="animate-pulse">
            <div className="h-12 bg-gray-100 mb-2"></div>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-gray-50 mb-1"></div>
            ))}
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="mb-4">
              {search || statusFilter || sourceFilter || assigneeFilter
                ? 'No leads match your filters'
                : 'No leads yet'}
            </p>
            <button className="btn btn-primary">Import Leads</button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500 bg-gray-50">
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Name {getSortIcon('name')}
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => handleSort('email')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Email {getSortIcon('email')}
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => handleSort('source')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Source {getSortIcon('source')}
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Status {getSortIcon('status')}
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => handleSort('score')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Score {getSortIcon('score')}
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => handleSort('assigned_to')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Assigned {getSortIcon('assigned_to')}
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => handleSort('created_at')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Created {getSortIcon('created_at')}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(lead => (
                    <tr
                      key={lead.id}
                      onClick={() => handleRowClick(lead.id)}
                      className="border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {lead.metadata?.name || 'Unknown'}
                        </div>
                        {lead.metadata?.company && (
                          <div className="text-sm text-gray-500">{lead.metadata.company}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {lead.metadata?.email || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-600">{sourceLabels[lead.source]}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[lead.status]}`}>
                          {statusLabels[lead.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-medium ${getScoreColor(lead.score)}`}>
                          {lead.score}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {lead.assigned_to || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm">
                        {formatLeadDate(lead.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <div className="text-sm text-gray-500">
                Showing {filteredLeads.length} of {pagination.total > 0 ? pagination.total : 'many'} leads
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="btn btn-secondary text-sm px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} {totalPages > 0 && `of ${totalPages}`}
                </span>
                <button
                  onClick={() => setPage(pagination.page + 1)}
                  disabled={!pagination.hasMore}
                  className="btn btn-secondary text-sm px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LeadList;
